<i18n>
{
  "ja": {
    "contact-warehouse": "この倉庫に問い合わせ",
    "title": "{location}の倉庫{access} 詳細情報｜全国の営業倉庫・貸し倉庫・ 物流倉庫を探すなら WareX",
    "h1": "{location}の倉庫{access} 詳細",
    "description": "{location}の倉庫{access}の詳細ページ。 WareXに無料登録すると、倉庫を探して、すぐに空き状況を確認できます。契約からご利用まで、サポート対応も充実した、新しい倉庫利用サービスです。",
    "interchange": "IC",
    "airport": "空港",
    "seaport": "港",
    "station": "駅",
    "warehouse-at": "{location}の倉庫",
    "min-from-origin": "{name}から車で{min}分"
  }
}
</i18n>

<template>
  <div class="warehouse bg-gray-100">
    <div class="flex-grow flex flex-col mb-4 w-full p-4 md:p-12 lg:px-40">
      <header ref="pageHeader" class="mt-5 md:mt-0">
        <a class="flex items-center text-ink text-11p font-semibold -ml-2 cursor-pointer" @click="goBack">
          <span class="material-icons">chevron_left</span>
          倉庫を探す</a>
        <div class="w-full">
          <h1 class="font-semibold text-lg md:text-2xl">
            {{ $t('warehouse.warehouseDetailInfo') }}
          </h1>
        </div>
      </header>
      <warehouse-detail v-if="warehouse" :warehouse="warehouse" />
    </div>
    <WarehouseContact :warehouse="warehouse" />
    <BreadcrumbsFoot :list="breadcrumbs" />
  </div>
</template>

<style lang="scss" scoped>
.contact-warehouse-link {
  @apply inline-flex justify-center items-center rounded-full text-center text-xs whitespace-no-wrap text-white bg-orange px-3;
  min-width: 5rem;
}

.contact-warehouse-link-icon {
  filter: invert(100%) sepia(100%) saturate(0%) hue-rotate(288deg) brightness(102%) contrast(102%);
  height: 1.25rem;
}

.shadow-top {
    box-shadow: 0 -2px 20px -5px rgba(115,115,115,0.75);
}
</style>

<script>
import WarehouseContact from '@/components/warehouses/contact';
import WarehouseDetail from '@/components/warehouses/detail';
import { isEmpty } from '@/components/utils/string';
import { locationAccessTypes } from '@/model/warehouse'
import BreadcrumbsFoot from "@/components/breadcrumbsFoot";
import Jsonld from '@/components/mixins/jsonld';
import Modal from '@/components/modal'

export default {
  components: { WarehouseContact, WarehouseDetail, BreadcrumbsFoot, Modal },
  mixins: [Jsonld],
  meta: {
    feature: 'search'
  },
  validate ({ params }) {
    return !!params.id;
  },
  data() {
    return { locationAccessTypes }
  },
  mounted() {
    const { warehouse } = this;

    if (warehouse?.address?.regionName) {
      localStorage.setItem('prefecture', warehouse.address.regionName);
    }
  },
  async asyncData({ params, error, $warex }) {
    const { id } = params
    if(!id) {
      throw new TypeError("id parameter is required");
    }
    const warehouse = await $warex.warehouse(id)
    return { warehouse }
  },
  computed: {
    breadcrumbs () {
      const { warehouse, access } = this
      const list = [{ url: '/warehouses/search/area', name: this.$t('nav.area') }];

      if (warehouse) {
        warehouse.address.regionName && list.push(
          { url: `/warehouses/search/area/${warehouse.address.regionName}`, name: this.$t('warehouse-at', { location: warehouse.address.regionName }) }
        );
        warehouse.address.area && list.push(
          { url: `/warehouses/search/area/${warehouse.address.regionName}/${warehouse.address.area}`, name: this.$t('warehouse-at', { location: warehouse.address.area }) }
        );
        list.push({ name: this.$t('h1', { location: `${warehouse.address.regionName}${warehouse.address.muniName}`, access }) });
      }

      return list;
    },
    locationItems () {
      return this.locationAccessTypes.reduce((options, type) => {
        const entry = this.warehouse.relativeLocations.find(rl => rl.type === type);
        if (entry) options.push(entry);
        return options;
      }, []);
    },
    accessData() {
      const { locationItems } = this
      return locationItems[locationItems.length - 1] || null
    },
    access() {
      const { accessData } = this
      let access
      if (accessData) {
        access = `（${this.$t(accessData.type)}:${this.$t('min-from-origin', { name: accessData.name, min: accessData.distance })}）`
      }
      return access
    },
    searchType () {
      return this.$route.query.ref === 'area' ? 'area' : 'map';
    },
    address() {
      return this.warehouse.address;
    },
    displayNumber() {
      return this.warehouse.displayNumber;
    },
    hasItemTypes() {
      return this.warehouse.itemTypes && this.warehouse.itemTypes.length > 0
    },
    hasLocations() {
      return this.warehouse.relativeLocations && this.warehouse.relativeLocations.length > 0
    },
    global() {
      return this.$store.getters['cms/global']
    },
    homepage() {
      return this.$store.getters['cms/homepage']
    },
    locationItems() {
      return this.locationAccessTypes.reduce((options, type) => {
        const entry = this.warehouse.relativeLocations.find(rl => rl.type === type);
        if (entry) options.push(entry);
        return options;
      }, []);
    },
    distanceLocation() {
      if (!this.hasLocations) return '';

      const typeList = ['interchange', 'seaport', 'station', 'airport'];
      let item;

      for (const type of typeList) {
        const result = this.locationItems.find((entry) => entry.type === type)
        if (result) {
          item = result;
          break;
        }
      }

      if (!item) { item = this.locationItems[0] }

      return this.$t('minFromOrigin', { name: item.name, min: item.distance });
    },
  },
  head() {
    const { access, accessData } = this
    const { seo } = this.homepage;
    const { defaultSeo } = this.global;
    const { address } = (this.warehouse || {})
    const location = `${address.regionName}${address.muniName}`
    const title = this.$t('title', { location, access });
    const description = this.$t('description', { location, access });
    const titleTemplate = title;
    const uniqKeyword = accessData && `${accessData.name},` || ''
    const metaKeywords = `${location},${uniqKeyword}${this.$t('keywords')}`;
    const fullSeo = {
      ...defaultSeo,
      ...seo,
      metaTitle : title,
      metaDescription: description,
      metaKeywords
    };

    return {
      ...(titleTemplate && { titleTemplate }),
      title,
      meta: this.$options.filters.cmsMetaTags(fullSeo),
    }
  },
  methods: {
    goBack() {
        this.$router.go(-1);
        console.log("There is no previous path.");
    }
  },
  jsonld() {
    const ret = []
    const breadcrumbs = this.getBreadcrumbsJsonld(this.breadcrumbs)
    breadcrumbs && ret.push(breadcrumbs)
    return ret
  }
}
</script>